import { useTranslation } from "react-i18next";
import { useProductAvailability } from "~/hooks/use-product-availability";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import {
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { BundleFormatRow } from "./bundle-format-row";
import type { BundleFormat } from "./bundle.utils";
import { useBundleSelection } from "./use-bundle-selection";

export interface BundleDrawerBodyProps {
  /** Formats to choose from, already in display order. */
  formats: BundleFormat[];
  isLoading: boolean;
  isError: boolean;
  /** The ISBN being viewed; always included and locked. */
  currentIsbn: string;
  /** Formats to pre-select when the drawer opens. */
  initialSelection: string[];
  /** Whether a bundle is already active (drives labels and turn-off). */
  isEditing: boolean;
  onConfirm: (isbns: string[]) => void;
  onTurnOff: () => void;
  onClose: () => void;
}

export const BundleDrawerBody = ({
  formats,
  isLoading,
  isError,
  currentIsbn,
  initialSelection,
  isEditing,
  onConfirm,
  onTurnOff,
  onClose,
}: BundleDrawerBodyProps) => {
  const { t } = useTranslation(["productDetails"]);
  const { isProductActive } = useProductAvailability();
  const formatsReady = !isLoading && !isError;

  const {
    selected,
    toggle,
    selectAll,
    clearAll,
    selectedIsbns,
    selectedCount,
    total,
    canConfirm,
    allSelected,
    isTurnOff,
  } = useBundleSelection({
    formats,
    currentIsbn,
    initialSelection,
    isEditing,
    formatsReady,
  });

  const handlePrimary = () => {
    if (!formatsReady) {
      return;
    }
    if (isTurnOff) {
      onTurnOff();
      return;
    }
    if (!canConfirm) {
      return;
    }
    onConfirm(selectedIsbns);
  };

  let primaryLabel = t("salesData.bundle.drawer.confirm");
  if (isTurnOff) {
    primaryLabel = t("salesData.bundle.drawer.turnOff");
  } else if (isEditing) {
    primaryLabel = t("salesData.bundle.drawer.save");
  }

  let body;
  if (isLoading) {
    body = (
      <Text variant="muted" className="py-6">
        {t("salesData.bundle.drawer.loading")}
      </Text>
    );
  } else if (isError) {
    body = (
      <Text variant="muted" className="py-6">
        {t("salesData.bundle.drawer.error")}
      </Text>
    );
  } else if (formats.length === 0) {
    body = (
      <Text variant="muted" className="py-6">
        {t("salesData.bundle.drawer.empty")}
      </Text>
    );
  } else {
    body = (
      <ul className="flex flex-col gap-2 py-2">
        {formats.map((format, index) => (
          <BundleFormatRow
            key={format.id}
            format={format}
            index={index}
            isCurrent={format.identifier === currentIsbn}
            isSelected={selected.has(format.identifier)}
            isActive={isProductActive(format.supplyDetails)}
            onToggle={toggle}
          />
        ))}
      </ul>
    );
  }

  return (
    <>
      <SheetHeader className="border-b px-6 py-4">
        <SheetTitle>{t("salesData.bundle.drawer.title")}</SheetTitle>
        <SheetDescription>
          {t("salesData.bundle.drawer.subtitle")}
        </SheetDescription>
      </SheetHeader>

      <Text
        as="div"
        variant="muted"
        className="flex items-center justify-between px-6 py-3"
      >
        <span>
          {t("salesData.bundle.drawer.included", {
            selected: selectedCount,
            total,
          })}
        </span>
        <Button
          variant="link"
          size="small"
          disabled={!formatsReady || total === 0}
          label={
            allSelected
              ? t("salesData.bundle.drawer.clearAll")
              : t("salesData.bundle.drawer.selectAll")
          }
          onClick={allSelected ? clearAll : selectAll}
          testId="bundle-drawer-select-toggle"
        />
      </Text>

      <div className="flex-1 overflow-y-auto px-6">{body}</div>

      <SheetFooter className="flex-row justify-end gap-2 border-t px-6 py-4">
        <Button
          variant="tertiary"
          label={t("salesData.bundle.drawer.cancel")}
          onClick={onClose}
          testId="bundle-drawer-cancel"
        />
        <Button
          variant={isTurnOff ? "danger" : "primary"}
          disabled={!isTurnOff && !canConfirm}
          label={primaryLabel}
          onClick={handlePrimary}
          testId="bundle-drawer-confirm"
        />
      </SheetFooter>
    </>
  );
};
